import { z } from 'zod';

/**
 * Deployment configuration shapes shared between the admin-console UI and
 * the backend `/api/admin/deployments` router.
 *
 * Storage location: `admin_config.deployments` JSONB column. The PAT inside
 * `github.pat` is encrypted-at-rest via `lib/crypto-vault.ts` before being
 * written; the raw value never leaves the API process.
 *
 * Response shapes deliberately omit the encrypted PAT — the UI only ever
 * sees a `pat_configured: boolean` flag so a paste-by-mistake doesn't show
 * up on a screen-share.
 */

export const DEPLOYMENT_ENVS = ['local', 'qa', 'uat', 'production'] as const;
export const DeploymentEnvIdSchema = z.enum(DEPLOYMENT_ENVS);
export type DeploymentEnvId = z.infer<typeof DeploymentEnvIdSchema>;

/**
 * Test stage gates passed straight through to `deploy.yml` inputs. Each
 * boolean toggles a `workflow_call` job in the reusable CI workflow:
 *   - unit:        vitest unit suites in `packages/*`
 *   - api:         API integration tests (vitest + supertest)
 *   - e2e:         Playwright suite in `packages/e2e`
 *   - performance: API benchmark (autocannon) + UI Lighthouse against
 *                  the just-deployed env. See `scripts/perf/`.
 *   - security:    deps audit + secret scan + SAST + (UAT/prod) ZAP DAST.
 *                  See `scripts/security/`.
 *
 * Default per env is conservative (everything on for qa+); the UI lets
 * ops toggle them off deliberately for hot-fix paths (audited).
 */
export const DeploymentTestTogglesSchema = z.object({
  unit: z.boolean(),
  api: z.boolean(),
  e2e: z.boolean(),
  performance: z.boolean().default(false),
  security: z.boolean().default(false),
});
export type DeploymentTestToggles = z.infer<typeof DeploymentTestTogglesSchema>;

/**
 * Snapshot of the most recent verification run (auto-smoke or on-demand
 * `verify.yml`). Used by the env card to show a "Last smoke" pill at a
 * glance. Persisted under `admin_config.deployments.envs[env].lastVerification`
 * and refreshed on every `GET /runs` reconciliation.
 */
export const DeploymentLastVerificationSchema = z.object({
  /** GitHub Actions run id, so the UI can deep-link to the run page. */
  runId: z.number(),
  /** `success` | `failure` | `cancelled` | null while still in flight. */
  conclusion: z.string().nullable(),
  /** `queued` | `in_progress` | `completed`. */
  status: z.string(),
  /** SHA of the commit the verification ran against. */
  sha: z.string(),
  branch: z.string(),
  htmlUrl: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  /** `'auto'` (post-deploy smoke that ran inside deploy.yml) | `'manual'` (verify.yml). */
  source: z.enum(['auto', 'manual']),
});
export type DeploymentLastVerification = z.infer<typeof DeploymentLastVerificationSchema>;

export const DeploymentEnvConfigSchema = z.object({
  env: DeploymentEnvIdSchema,
  /** Branch name pushed to GitHub Actions as `ref`. Defaults to `main`. */
  branch: z.string().min(1).max(255),
  /** Whether `npm run db:migrate` is invoked after the new images come up. */
  runMigrations: z.boolean(),
  /** Whether the `@smoke`-tagged Playwright pass runs against the just-deployed env. */
  postDeploySmoke: z.boolean(),
  tests: DeploymentTestTogglesSchema,
  /** Free-form note shown on the env card (deploy contact, on-call, etc.). */
  notes: z.string().max(500).optional().default(''),
  /** Optional override of the public URL used for `/api/health` proxying. */
  healthUrl: z.string().url().optional(),
  /**
   * SHA of the most recent deploy whose post-deploy smoke (or manual
   * verification) passed. Used as the rollback target when the latest
   * deploy is broken.
   */
  lastSuccessfulSha: z.string().optional(),
  /**
   * SHA of the deploy currently running on this env. May or may not equal
   * `lastSuccessfulSha` depending on whether the latest verification has
   * passed yet.
   */
  currentSha: z.string().optional(),
  lastVerification: DeploymentLastVerificationSchema.optional(),
});
export type DeploymentEnvConfig = z.infer<typeof DeploymentEnvConfigSchema>;

/** Update payload for `PUT /api/admin/deployments/envs/:env`. Partial. */
export const DeploymentEnvUpdateSchema = DeploymentEnvConfigSchema.partial().omit({ env: true });
export type DeploymentEnvUpdate = z.infer<typeof DeploymentEnvUpdateSchema>;

/**
 * GitHub credentials. PAT is write-only on the API: writes accept `pat`,
 * reads expose `pat_configured` only. Repo coordinates are plaintext —
 * they're not secret and the UI needs them to render the recent-runs links.
 */
export const DeploymentGithubCredsReadSchema = z.object({
  owner: z.string(),
  repo: z.string(),
  workflowFile: z.string(),
  patConfigured: z.boolean(),
});
export type DeploymentGithubCredsRead = z.infer<typeof DeploymentGithubCredsReadSchema>;

export const DeploymentGithubCredsWriteSchema = z.object({
  owner: z.string().min(1).max(255),
  repo: z.string().min(1).max(255),
  workflowFile: z.string().min(1).max(255).default('deploy.yml'),
  /** New PAT value. Empty/omitted = leave existing token unchanged. */
  pat: z.string().min(0).max(512).optional(),
});
export type DeploymentGithubCredsWrite = z.infer<typeof DeploymentGithubCredsWriteSchema>;

/**
 * `POST /api/admin/deployments/:env/deploy` body. All fields optional —
 * unspecified values fall back to the saved env config so a one-click
 * deploy from the UI is just an empty body.
 */
export const DeploymentDispatchSchema = z.object({
  branch: z.string().min(1).max(255).optional(),
  tag: z.string().max(255).optional(),
  runMigrations: z.boolean().optional(),
  postDeploySmoke: z.boolean().optional(),
  tests: DeploymentTestTogglesSchema.partial().optional(),
});
export type DeploymentDispatch = z.infer<typeof DeploymentDispatchSchema>;

/**
 * Slim summary of a GitHub Actions workflow run, returned by
 * `GET /:env/runs` and the audit log proxy. Shape mirrors the GitHub REST
 * response so the UI can switch to the official endpoint with no
 * adapter layer if we ever drop the proxy.
 */
export const DeploymentRunSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  displayTitle: z.string(),
  status: z.string(),
  conclusion: z.string().nullable(),
  htmlUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  headSha: z.string(),
  headBranch: z.string(),
  event: z.string(),
  /** Resolved by the backend from saved dispatch metadata. */
  env: DeploymentEnvIdSchema.nullable(),
});
export type DeploymentRunSummary = z.infer<typeof DeploymentRunSummarySchema>;

/**
 * Per-subsystem result inside the deep-health probe. `ok=true` is healthy,
 * `ok=false` carries an actionable `detail` string. `null` means the probe
 * was skipped (e.g. SMTP not configured for an env).
 */
export const DeploymentSubsystemHealthSchema = z.object({
  ok: z.boolean(),
  detail: z.string().nullable(),
  latencyMs: z.number().nullable(),
});
export type DeploymentSubsystemHealth = z.infer<typeof DeploymentSubsystemHealthSchema>;

/**
 * Deep-health payload returned by the target API at `/api/health/deep`. The
 * admin server proxies this so the dashboard can render a per-subsystem
 * checklist. Optional fields are omitted on the target if the corresponding
 * subsystem isn't configured (e.g. no SMTP host set).
 */
export const DeploymentDeepHealthSchema = z.object({
  status: z.string(),
  service: z.string(),
  version: z.string().optional(),
  gitSha: z.string().optional(),
  uptimeSec: z.number().optional(),
  env: z.string().optional(),
  subsystems: z.object({
    database: DeploymentSubsystemHealthSchema,
    smtp: DeploymentSubsystemHealthSchema.nullable(),
    runner: DeploymentSubsystemHealthSchema.nullable(),
    scheduler: DeploymentSubsystemHealthSchema.nullable(),
  }),
});
export type DeploymentDeepHealth = z.infer<typeof DeploymentDeepHealthSchema>;

/** Health proxy response. Wraps the upstream payload + adds latency. */
export const DeploymentHealthSchema = z.object({
  reachable: z.boolean(),
  status: z.string().nullable(),
  database: z.string().nullable(),
  version: z.string().nullable(),
  latencyMs: z.number().nullable(),
  checkedAt: z.string(),
  /** Populated when /api/health/deep is reachable on the target. */
  deep: DeploymentDeepHealthSchema.nullable().optional(),
});
export type DeploymentHealth = z.infer<typeof DeploymentHealthSchema>;

/**
 * `POST /api/admin/deployments/:env/verify` body. All fields optional —
 * the saved env config and the most recent successful deploy fill in the
 * blanks, so a one-click "Verify" click sends an empty body.
 */
export const DeploymentVerifyDispatchSchema = z.object({
  /** Branch / ref to check out before running the smoke suite. */
  branch: z.string().min(1).max(255).optional(),
  /** Override the target URL the smoke suite hits (defaults to the env's public URL). */
  baseUrl: z.string().url().optional(),
});
export type DeploymentVerifyDispatch = z.infer<typeof DeploymentVerifyDispatchSchema>;

/**
 * `POST /api/admin/deployments/:env/rollback` body. The backend defaults
 * the target sha to the env's `lastSuccessfulSha`; specifying it explicitly
 * lets ops roll back to an arbitrary earlier deploy.
 */
export const DeploymentRollbackSchema = z.object({
  /** Image tag / commit SHA to redeploy. Falls back to `lastSuccessfulSha`. */
  tag: z.string().min(1).max(255).optional(),
  /** Branch to point HEAD at on the VM. Defaults to the env's saved branch. */
  branch: z.string().min(1).max(255).optional(),
  /** Skip migrations on the rollback. Defaults to `false` (run them). */
  skipMigrations: z.boolean().optional(),
});
export type DeploymentRollback = z.infer<typeof DeploymentRollbackSchema>;

/**
 * Default config used both by the API on first-write and by the UI to
 * pre-populate empty cards. Owners/admins always pick a branch and decide
 * which test stages run before clicking "Deploy", but the defaults here
 * reflect the conservative "everything on" stance.
 */
export function defaultDeploymentEnvConfig(env: DeploymentEnvId): DeploymentEnvConfig {
  return {
    env,
    // Local + QA track integration branches; UAT/Prod start on `main` and
    // ops can pin them to release branches as needed.
    branch: env === 'local' || env === 'qa' ? 'main' : 'main',
    runMigrations: true,
    postDeploySmoke: env !== 'local',
    tests: {
      unit: true,
      api: true,
      e2e: env !== 'local',
      // Performance defaults: on for qa (cheap) + uat (canary). Off
      // for prod by default — most prod deploys are hot-fixes where
      // the perf signal is already known and an extra 5-min job hurts
      // MTTR. Operators can flip it on per-deploy via the UI.
      performance: env === 'qa' || env === 'uat',
      // Security defaults: on for qa + uat + prod. The deps + secret +
      // SAST steps are offline and quick (~2 min); the DAST step is
      // gated separately on UAT only, see deploy.yml.
      security: env !== 'local',
    },
    notes: '',
  };
}
